import React, { useState, useEffect, Fragment } from 'react';
import { toast } from 'react-hot-toast';
import { 
  Dialog, DialogPanel, DialogTitle, Transition, TransitionChild,
  Field, Label, Input, Textarea, Button
} from '@headlessui/react';
import { FaTimes, FaCheck } from 'react-icons/fa';
import LandingPagePreview from './LandingPagePreview';

const LandingPageForm = ({ isOpen, onClose, onSubmit, project, initialData = null, isLoading = false, onDelete = null }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    llmsTxt: '',
    customUrl: '',
    projectId: project?._id || '',
    supportModuleId: '',
    status: 'draft',
    isActive: true,
    selectedDocuments: {
      privacyPolicy: '',
      termsOfService: '',
      disclaimer: '',
      cookiePolicy: '',
      refundPolicy: '',
      dataProcessingAgreement: ''
    },
    theme: {
      primaryColor: '#3B82F6',
      secondaryColor: '#1E40AF',
      backgroundColor: '#FFFFFF',
      textColor: '#1F2937',
      accentColor: '#10B981'
    }
  });
  const [loading, setLoading] = useState(false);
  const [urlAvailable, setUrlAvailable] = useState(true);
  const [checkingUrl, setCheckingUrl] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [supportModules, setSupportModules] = useState([]);
  const [loadingSupportModules, setLoadingSupportModules] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const initializeForm = async () => {
      console.log('LandingPageForm useEffect called', { initialData, project });
      // Always fetch projects first
      await fetchProjects();
      console.log('Projects fetched, setting form data...');
      
    if (initialData) {
        console.log('Setting form data for edit mode:', {
          initialData,
          projectId: initialData.projectId,
          projects: projects.length
        });
      setFormData({
        title: initialData.title || project?.name || '',
        description: initialData.description || project?.description || '',
        llmsTxt: initialData.llmsTxt || '',
        customUrl: initialData.customUrl || '',
          projectId: initialData.projectId?._id || initialData.projectId || project?._id || '',
          supportModuleId: initialData.supportModuleId || '',
          status: initialData.status || 'draft',
          isActive: initialData.isActive !== undefined ? initialData.isActive : true,
          selectedDocuments: {
            privacyPolicy: initialData.selectedDocuments?.privacyPolicy || '',
            termsOfService: initialData.selectedDocuments?.termsOfService || '',
            disclaimer: initialData.selectedDocuments?.disclaimer || initialData.selectedDocuments?.userAgreement || '',
            cookiePolicy: initialData.selectedDocuments?.cookiePolicy || '',
            refundPolicy: initialData.selectedDocuments?.refundPolicy || '',
            dataProcessingAgreement: initialData.selectedDocuments?.dataProcessingAgreement || ''
          },
        theme: {
          primaryColor: initialData.theme?.primaryColor || '#3B82F6',
          secondaryColor: initialData.theme?.secondaryColor || '#1E40AF',
          backgroundColor: initialData.theme?.backgroundColor || '#FFFFFF',
          textColor: initialData.theme?.textColor || '#1F2937',
          accentColor: initialData.theme?.accentColor || '#10B981'
        }
      });
        console.log('Form data set:', {
          projectId: initialData.projectId,
          title: initialData.title,
          customUrl: initialData.customUrl
        });
        
        // Edit mode'da da support module'ları ve document'ları fetch et
        const projectId = initialData.projectId?._id || initialData.projectId || project?._id;
        if (projectId) {
          fetchSupportModules(projectId);
          fetchDocuments(projectId);
        }
    } else if (project) {
      setFormData(prev => ({
        ...prev,
        projectId: project._id,
        title: project.name,
        description: project.description
      }));
        
        // Create mode'da da support module'ları ve document'ları fetch et
        fetchSupportModules(project._id);
        fetchDocuments(project._id);
      }
    };

    if (isOpen) {
      initializeForm();
    }
  }, [project, initialData, isOpen]);

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      console.log('Fetching projects...');
      const response = await fetch('/api/dashboard/user/projects?fields=id,name', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Projects fetched:', data);
        setProjects(data.projects || []);
        return data.projects || [];
      } else {
        console.error('Failed to fetch projects:', response.status);
        toast.error('Failed to load projects');
        return [];
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Error loading projects');
      return [];
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchSupportModules = async (projectId) => {
    if (!projectId) {
      setSupportModules([]);
      return;
    }

    try {
      setLoadingSupportModules(true);
      console.log('Fetching support modules for project:', projectId);
      const response = await fetch(`/api/support/user/support-modules?projectId=${projectId}&isActive=true`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Support modules fetched:', data);
        setSupportModules(data.supportModules || []);
        return data.supportModules || [];
      } else {
        console.error('Failed to fetch support modules:', response.status);
        return [];
      }
    } catch (error) {
      console.error('Error fetching support modules:', error);
      return [];
    } finally {
      setLoadingSupportModules(false);
    }
  };

  const fetchDocuments = async (projectId) => {
    if (!projectId) {
      setDocuments([]);
      return;
    }

    try {
      setLoadingDocuments(true);
      console.log('Fetching documents for project:', projectId);
      console.log('API URL:', `/api/dashboard/user/projects/${projectId}/documents`);
      const response = await fetch(`/api/dashboard/user/projects/${projectId}/documents`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('Documents API response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('Documents fetched:', data);
        console.log('Documents count:', data.documents?.length || 0);
        setDocuments(data.documents || []);
        return data.documents || [];
      } else {
        const errorData = await response.json();
        console.error('Failed to fetch documents:', response.status, errorData);
        return [];
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      return [];
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value || ''
    }));
  };

  const handleProjectChange = (e) => {
    const projectId = e.target.value;
    const selectedProject = projects.find(p => p._id === projectId);
    
    setFormData(prev => ({
      ...prev,
      projectId,
      title: selectedProject ? selectedProject.name : prev.title,
      description: selectedProject ? selectedProject.description : prev.description,
      supportModuleId: '' // Reset support module when project changes
    }));

    // Fetch support modules and documents for the selected project
    fetchSupportModules(projectId);
    fetchDocuments(projectId);
  };

  const handleColorChange = (colorName, value) => {
    setFormData(prev => ({
      ...prev,
      theme: {
        ...prev.theme,
        [colorName]: value
      }
    }));
  };

  const handleDocumentChange = (documentType, documentId) => {
    setFormData(prev => ({
      ...prev,
      selectedDocuments: {
        ...prev.selectedDocuments,
        [documentType]: documentId
      }
    }));
  };

  const checkUrlAvailability = async (url) => {
    if (!url || url.length < 3) {
      setUrlAvailable(true);
      return;
    }

    setCheckingUrl(true);
    try {
      const response = await fetch(`/api/landing-pages/check-url/${url}${initialData ? `?excludeId=${initialData._id}` : ''}`);
      const data = await response.json();
      setUrlAvailable(data.available);
    } catch (error) {
      console.error('Error checking URL:', error);
      setUrlAvailable(false);
    } finally {
      setCheckingUrl(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('LandingPageForm handleSubmit called', { formData, initialData });
    console.log('Form validation starting...');
    
    if (!formData.title || !formData.title.trim()) {
      console.log('Title validation failed');
      toast.error('Title is required');
      return;
    }

    if (!formData.projectId) {
      console.log('Project validation failed');
      toast.error('Project selection is required');
      return;
    }

    if (!formData.customUrl || !formData.customUrl.trim()) {
      console.log('Custom URL is required');
      toast.error('Custom URL is required');
      return;
    }

    if (formData.customUrl && !urlAvailable) {
      console.log('Custom URL validation failed');
      toast.error('Custom URL is not available');
      return;
    }
    
    console.log('All validations passed, proceeding with API call...');
    console.log('Selected documents being sent:', formData.selectedDocuments);

    setLoading(true);
    try {
      const payload = {
        projectId: formData.projectId,
        ...formData
      };

      if (initialData) {
        // Update existing landing page
        const response = await fetch(`/api/landing-pages/${initialData._id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update landing page');
        }
      } else {
        // Create new landing page
        console.log('Creating new landing page with payload:', payload);
        console.log('Making API call to /api/landing-pages...');
        
        // Add timeout to prevent hanging
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          console.log('API call timed out after 10 seconds');
          controller.abort();
        }, 10000); // 10 second timeout
        
        try {
          console.log('About to make fetch request...');
        const response = await fetch('/api/landing-pages', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
            body: JSON.stringify(payload),
            signal: controller.signal
        });

          clearTimeout(timeoutId);
          console.log('Create response received:', response.status, response.statusText);

        console.log('Create response received:', response.status, response.statusText);
        if (!response.ok) {
          const errorData = await response.json();
          console.error('Create error:', errorData);
          throw new Error(errorData.message || 'Failed to create landing page');
        } else {
          console.log('Create successful!');
        }
        } catch (fetchError) {
          clearTimeout(timeoutId);
          if (fetchError.name === 'AbortError') {
            console.error('API call timed out after 10 seconds');
            throw new Error('API call timed out. Please try again.');
          } else {
            console.error('Fetch error:', fetchError);
            throw fetchError;
          }
        }
      }

      toast.success(initialData ? 'Landing page updated successfully' : 'Landing page created successfully');
      onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error saving landing page:', error);
      toast.error(error.message || 'Failed to save landing page');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!initialData || !onDelete) return;
    
    setDeleting(true);
    try {
      await onDelete(initialData._id);
      setShowDeleteDialog(false);
      onClose();
      toast.success('Landing page deleted successfully!');
    } catch (error) {
      console.error('Error deleting landing page:', error);
      toast.error('Failed to delete landing page');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-7xl max-h-[90vh] transform overflow-y-auto rounded-2xl bg-gray-900 border border-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-6">
                  <DialogTitle as="h3" className="text-lg font-medium text-white">
        {initialData ? 'Edit Landing Page' : 'Create Landing Page'}
                  </DialogTitle>
                  <Button
                    onClick={onClose}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors duration-200 data-hover:bg-gray-800 data-focus:ring-white"
                  >
                    <FaTimes className="w-5 h-5" />
                  </Button>
                </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Sol sütun - Form */}
        <div className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          
                    <Field>
                      <Label className="block text-sm font-medium text-gray-300 mb-2">
                Project *
                      </Label>
              <select
                name="projectId"
                value={formData.projectId}
                        onChange={handleProjectChange}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
                        disabled={loadingProjects || initialData} // Disabled in edit mode
              >
                <option value="">Select a project</option>
                        {projects && Array.isArray(projects) && projects.length > 0 ? projects.map((proj) => {
                          if (!proj || typeof proj !== 'object' || !proj._id) return null;
                          return (
                  <option key={proj._id} value={proj._id}>
                              {proj.name || 'Unknown Project'}
                  </option>
                          );
                        }).filter(Boolean) : (
                          <option value="" disabled>No projects available</option>
                        )}
              </select>
              {loadingProjects && (
                <p className="text-xs text-gray-400 mt-1">Loading projects...</p>
              )}
                      {initialData && (
                        <p className="text-xs text-gray-400 mt-1">Project cannot be changed in edit mode</p>
                      )}
                    </Field>
                    
                    <Field>
                      <Label className="block text-sm font-medium text-gray-300 mb-2">
                        Custom URL *
                      </Label>
                      <div className="flex items-center space-x-2">
                        <Input
              type="text"
                          name="customUrl"
                          value={formData.customUrl}
              onChange={handleInputChange}
                          className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="my-custom-url"
              required
            />
                        {checkingUrl && (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-500"></div>
                        )}
                        {!checkingUrl && formData.customUrl && (
                          <div className="flex items-center">
                            {urlAvailable ? (
                              <FaCheck className="text-green-500" />
                            ) : (
                              <FaTimes className="text-red-500" />
                            )}
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {formData.customUrl ? (
                          <span>
                            Your landing page will be available at: <span className="text-indigo-400 font-mono">{window.location.origin}/landing/{formData.customUrl}</span>
                          </span>
                        ) : (
                          'Leave empty to use the landing page title as URL'
                        )}
                      </p>
                    </Field>

                    <Field>
                      <Label className="block text-sm font-medium text-gray-300 mb-2">
                        Support Module (Optional)
                      </Label>
                      <select
                        name="supportModuleId"
                        value={formData.supportModuleId}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        disabled={loadingSupportModules || !formData.projectId}
                      >
                        <option value="">No support module</option>
                        {supportModules && Array.isArray(supportModules) && supportModules.length > 0 ? supportModules.map((module) => {
                          if (!module || typeof module !== 'object' || !module._id) return null;
                          return (
                            <option key={module._id} value={module._id}>
                              {module.name || 'Unknown Support Module'}
                            </option>
                          );
                        }).filter(Boolean) : (
                          <option value="" disabled>
                            {loadingSupportModules ? 'Loading support modules...' : 'No support modules available'}
                          </option>
                        )}
                      </select>
                      <p className="text-xs text-gray-400 mt-1">
                        Select a support module to enable customer support on your landing page
                      </p>
                    </Field>

                    {/* Document Selection */}
                    <div className="space-y-4">
                      <h4 className="text-md font-medium text-white">Document Selection</h4>
                      <p className="text-sm text-gray-400">Select documents to display in the footer of your landing page</p>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <Field>
                          <Label className="block text-sm font-medium text-gray-300 mb-2">
                            Privacy Policy
                          </Label>
                          <select
                            value={formData.selectedDocuments.privacyPolicy}
                            onChange={(e) => handleDocumentChange('privacyPolicy', e.target.value)}
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            disabled={loadingDocuments || !formData.projectId}
                          >
                            <option value="">No document selected</option>
                            {documents && Array.isArray(documents) && documents.length > 0 ? documents
                              .filter(doc => doc.documentType === 'privacy_policy')
                              .map((doc) => (
                                <option key={doc._id} value={doc._id}>
                                  {doc.title || 'Unknown Document'}
                                </option>
                              )) : (
                              <option value="" disabled>
                                {loadingDocuments ? 'Loading documents...' : 'No privacy policy documents available'}
                              </option>
                            )}
                          </select>
                        </Field>

                        <Field>
                          <Label className="block text-sm font-medium text-gray-300 mb-2">
                            Terms of Service
                          </Label>
                          <select
                            value={formData.selectedDocuments.termsOfService}
                            onChange={(e) => handleDocumentChange('termsOfService', e.target.value)}
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            disabled={loadingDocuments || !formData.projectId}
                          >
                            <option value="">No document selected</option>
                            {documents && Array.isArray(documents) && documents.length > 0 ? documents
                              .filter(doc => doc.documentType === 'terms_of_service')
                              .map((doc) => (
                                <option key={doc._id} value={doc._id}>
                                  {doc.title || 'Unknown Document'}
                                </option>
                              )) : (
                              <option value="" disabled>
                                {loadingDocuments ? 'Loading documents...' : 'No terms of service documents available'}
                              </option>
                            )}
                          </select>
                        </Field>

                        <Field>
                          <Label className="block text-sm font-medium text-gray-300 mb-2">
                            Disclaimer
                          </Label>
                          <select
                            value={formData.selectedDocuments.disclaimer}
                            onChange={(e) => handleDocumentChange('disclaimer', e.target.value)}
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            disabled={loadingDocuments || !formData.projectId}
                          >
                            <option value="">No document selected</option>
                            {documents && Array.isArray(documents) && documents.length > 0 ? documents
                              .filter(doc => doc.documentType === 'disclaimer')
                              .map((doc) => (
                                <option key={doc._id} value={doc._id}>
                                  {doc.title || 'Unknown Document'}
                                </option>
                              )) : (
                              <option value="" disabled>
                                {loadingDocuments ? 'Loading documents...' : 'No disclaimer documents available'}
                              </option>
                            )}
                          </select>
                        </Field>

                        <Field>
                          <Label className="block text-sm font-medium text-gray-300 mb-2">
                            Cookie Policy
                          </Label>
                          <select
                            value={formData.selectedDocuments.cookiePolicy}
                            onChange={(e) => handleDocumentChange('cookiePolicy', e.target.value)}
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            disabled={loadingDocuments || !formData.projectId}
                          >
                            <option value="">No document selected</option>
                            {documents && Array.isArray(documents) && documents.length > 0 ? documents
                              .filter(doc => doc.documentType === 'cookie_policy')
                              .map((doc) => (
                                <option key={doc._id} value={doc._id}>
                                  {doc.title || 'Unknown Document'}
                                </option>
                              )) : (
                              <option value="" disabled>
                                {loadingDocuments ? 'Loading documents...' : 'No cookie policy documents available'}
                              </option>
                            )}
                          </select>
                        </Field>

                        <Field>
                          <Label className="block text-sm font-medium text-gray-300 mb-2">
                            Refund Policy
                          </Label>
                          <select
                            value={formData.selectedDocuments.refundPolicy}
                            onChange={(e) => handleDocumentChange('refundPolicy', e.target.value)}
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            disabled={loadingDocuments || !formData.projectId}
                          >
                            <option value="">No document selected</option>
                            {documents && Array.isArray(documents) && documents.length > 0 ? documents
                              .filter(doc => doc.documentType === 'refund_policy')
                              .map((doc) => (
                                <option key={doc._id} value={doc._id}>
                                  {doc.title || 'Unknown Document'}
                                </option>
                              )) : (
                              <option value="" disabled>
                                {loadingDocuments ? 'Loading documents...' : 'No refund policy documents available'}
                              </option>
                            )}
                          </select>
                        </Field>

                        <Field>
                          <Label className="block text-sm font-medium text-gray-300 mb-2">
                            Data Processing Agreement
                          </Label>
                          <select
                            value={formData.selectedDocuments.dataProcessingAgreement}
                            onChange={(e) => handleDocumentChange('dataProcessingAgreement', e.target.value)}
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            disabled={loadingDocuments || !formData.projectId}
                          >
                            <option value="">No document selected</option>
                            {documents && Array.isArray(documents) && documents.length > 0 ? documents
                              .filter(doc => doc.documentType === 'data_processing_agreement')
                              .map((doc) => (
                                <option key={doc._id} value={doc._id}>
                                  {doc.title || 'Unknown Document'}
                                </option>
                              )) : (
                              <option value="" disabled>
                                {loadingDocuments ? 'Loading documents...' : 'No data processing agreement documents available'}
                              </option>
                            )}
                          </select>
                        </Field>
                      </div>
          </div>

                    {/* Status and Active fields - only for edit mode */}
                    {initialData && (
                      <>
                        <Field>
                          <Label className="block text-sm font-medium text-gray-300 mb-2">
                            Status
                          </Label>
                          <select
                            name="status"
                            value={formData.status}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="draft">Draft</option>
                            <option value="published">Published</option>
                            <option value="archived">Archived</option>
                          </select>
                        </Field>

                      </>
                    )}

                    <Field>
                      <Label className="block text-sm font-medium text-gray-300 mb-2">
              Title *
                      </Label>
                      <Input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Your App Title"
              required
            />
                    </Field>

                    <Field>
                      <Label className="block text-sm font-medium text-gray-300 mb-2">
              Description
                      </Label>
                      <Textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Brief description of your app"
            />
                    </Field>

                    <Field>
                      <Label className="block text-sm font-medium text-gray-300 mb-2">
                        LLMs.txt Content
                      </Label>
                      <Textarea
                        name="llmsTxt"
                        value={formData.llmsTxt}
                        onChange={handleInputChange}
                        rows={10}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="# Project name\n\nDescribe this landing page for AI assistants and crawlers..."
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        Public URL: /landing/{formData.customUrl || 'your-url'}/llms.txt
                      </p>
                    </Field>

        </div>

        {/* Theme Colors */}
        <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white">Theme Colors</h3>
          
          <div className="grid grid-cols-2 gap-4">
                      <Field>
                        <Label className="block text-sm font-medium text-gray-300 mb-2">
                Primary Color
                        </Label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  value={formData.theme.primaryColor}
                            onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                            className="w-10 h-10 rounded border border-gray-600 cursor-pointer"
                />
                          <Input
                  type="text"
                  value={formData.theme.primaryColor}
                            onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                            className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
                      </Field>

                      <Field>
                        <Label className="block text-sm font-medium text-gray-300 mb-2">
                Secondary Color
                        </Label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  value={formData.theme.secondaryColor}
                            onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                            className="w-10 h-10 rounded border border-gray-600 cursor-pointer"
                />
                          <Input
                  type="text"
                  value={formData.theme.secondaryColor}
                            onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                            className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
                      </Field>

                      <Field>
                        <Label className="block text-sm font-medium text-gray-300 mb-2">
                Background Color
                        </Label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  value={formData.theme.backgroundColor}
                            onChange={(e) => handleColorChange('backgroundColor', e.target.value)}
                            className="w-10 h-10 rounded border border-gray-600 cursor-pointer"
                />
                          <Input
                  type="text"
                  value={formData.theme.backgroundColor}
                            onChange={(e) => handleColorChange('backgroundColor', e.target.value)}
                            className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
                      </Field>

                      <Field>
                        <Label className="block text-sm font-medium text-gray-300 mb-2">
                Text Color
                        </Label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  value={formData.theme.textColor}
                            onChange={(e) => handleColorChange('textColor', e.target.value)}
                            className="w-10 h-10 rounded border border-gray-600 cursor-pointer"
                />
                          <Input
                  type="text"
                  value={formData.theme.textColor}
                            onChange={(e) => handleColorChange('textColor', e.target.value)}
                            className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
                      </Field>
          </div>
        </div>

        {/* Actions */}
                  <div className="flex justify-between pt-6 border-t border-gray-700">
                    {/* Left side - Delete button (only for edit mode) */}
                    <div>
                      {initialData && onDelete && (
                        <Button
                          type="button"
                          onClick={() => setShowDeleteDialog(true)}
                          className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors font-medium"
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                    
                    {/* Right side - Cancel and Save buttons */}
                    <div className="flex space-x-3">
                      <Button
            type="button"
                        onClick={onClose}
                        className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors font-medium"
          >
            Cancel
                      </Button>
                      <Button
            type="submit"
                        disabled={isLoading || (formData.customUrl && !urlAvailable)}
                        className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 transition-colors font-medium"
          >
                        {isLoading ? 'Saving...' : (initialData ? 'Update' : 'Create')}
                      </Button>
                    </div>
        </div>
          </form>
        </div>

        {/* Sağ sütun - Önizleme */}
        <div className="space-y-6">
          <div className="sticky top-6">
            <h3 className="text-lg font-semibold text-white mb-4">Live Preview</h3>
            {formData.customUrl && (
              <div className="mb-4 p-3 bg-gray-800 rounded-lg">
                <p className="text-sm text-gray-300 mb-1">Preview URL:</p>
                <p className="text-indigo-400 font-mono text-sm">
                  {window.location.origin}/landing/{formData.customUrl}
                </p>
              </div>
            )}
            <div className="bg-white rounded-lg shadow-lg overflow-auto" style={{ minHeight: '600px', maxHeight: '80vh' }}>
              <LandingPagePreview formData={formData} documents={documents} projects={projects} />
            </div>
          </div>
        </div>
      </div>
              </DialogPanel>
            </TransitionChild>
          </div>
    </div>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Transition show={showDeleteDialog} as={Fragment}>
        <Dialog onClose={() => setShowDeleteDialog(false)} className="relative z-50">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-50" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-gray-900 border border-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center justify-between mb-4">
                    <DialogTitle as="h3" className="text-lg font-medium text-white">
                      Delete Landing Page
                    </DialogTitle>
                    <Button
                      onClick={() => setShowDeleteDialog(false)}
                      className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors duration-200"
                    >
                      <FaTimes className="w-5 h-5" />
                    </Button>
                  </div>
                  
                  <p className="text-gray-400 mb-6">
                    Are you sure you want to delete "{initialData?.title}"? This action cannot be undone.
                  </p>
                  
                  <div className="flex justify-end space-x-3">
                    <Button
                      onClick={() => setShowDeleteDialog(false)}
                      className="px-4 py-2 text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors duration-200"
                      disabled={deleting}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleDelete}
                      className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors duration-200"
                      disabled={deleting}
                    >
                      {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Transition>
  );
};

export default LandingPageForm;
